import React, { useEffect } from 'react'
import {
  setCrossClientResults,
  setCrossClientClients,
  setCrossClientResultsExecution,
  setCrossClientResultsExecutionLoading,
  setCrossClientTeammates,
  setCrossClientUsersLoading,
  stopCrossClientExecutionPolling,
  resetCrossClientResults,
} from '../../../store/crossClient/crossClientResults'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery, useLazyGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'
import { useLazyGetAllExternalUsersQuery } from '../../../store/externalUser/externalUser'

export interface CrossClientResultsInitializationProps {
  executionId: string
}

export const CrossClientResultsInitialization: React.FC<CrossClientResultsInitializationProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetAllCrossClientQueryResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()
  const [triggerGetAllContacts] = useLazyGetAllExternalUsersQuery()

  useEffect(() => {
    const getCrossClientResults = async () => {
      try {
        const results = await triggerGetAllCrossClientQueryResults({ accountId, executionId }).unwrap()
        if (!results.data?.length) {
          return
        }
        dispatch(setCrossClientResults(results.data))
      } catch (e) {
        console.error(e)
        dispatch(stopCrossClientExecutionPolling())
      }
    }
    const getExecution = async () => {
      try {
        dispatch(setCrossClientResultsExecutionLoading(true))
        const execution = await triggerGetCrossClientQueryExecution({ accountId, executionId }).unwrap()
        dispatch(setCrossClientResultsExecution(execution))
        await getCrossClientResults()
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientResultsExecutionLoading(false))
      }
    }
    getExecution()
  }, [dispatch, accountId, executionId, triggerGetCrossClientQueryExecution, triggerGetAllCrossClientQueryResults])

  useEffect(() => {
    const getCrossClientUsers = async () => {
      try {
        const teammatesQuery = triggerGetAllUsers({ accountId, identityTypes: ['idp'] }).unwrap()
        const contactsQuery = triggerGetAllContacts({ accountId }).unwrap()
        const [teammates, contacts] = await Promise.all([teammatesQuery, contactsQuery])
        if (teammates?.data) {
          dispatch(setCrossClientTeammates(teammates.data))
        }
        if (contacts?.data) {
          const clients = contacts.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))
          dispatch(setCrossClientClients(clients))
        }
      } finally {
        dispatch(setCrossClientUsersLoading(false))
      }
    }
    getCrossClientUsers()
  }, [dispatch, accountId, triggerGetAllUsers, triggerGetAllContacts])

  useEffect(() => {
    return () => {
      dispatch(resetCrossClientResults())
    }
  }, [dispatch])

  return null
}
